// The poster wall, as an object you can walk up to.
//
// Nights were advertised only in a DOM overlay on the N key, which made
// "printed and hung" a fiction the world did not carry. A flyer is an
// object with a palette its materials produced; it deserves somewhere to
// be, and a settlement deserves a wall that tells you what is on tonight
// without opening a menu.
//
// One board, one canvas texture, repainted whenever the bill changes.
// The texture is owned here rather than handed to the scene's Tracker:
// Tracker only frees at scene teardown, so a wall that repaints every
// time somebody prints a flyer would leak a texture per print.

import { type Aabb, type NightBill, POSTER_WALL, POSTER_WALL_HEIGHT } from "@bebop/shared";
import * as THREE from "three";
import { disposeAuthoredObject, instanceAuthoredAsset } from "./authored-assets";

/**
 * The grid grows with the bill rather than being fixed. A quiet week with
 * one Night on it should hang one big poster, not one stamp in the corner
 * of eight empty slots.
 */
const SLOTS = 8;
function gridFor(count: number): { columns: number; rows: number } {
  if (count <= 1) return { columns: 1, rows: 1 };
  if (count <= 2) return { columns: 2, rows: 1 };
  if (count <= 4) return { columns: 2, rows: 2 };
  return { columns: 4, rows: 2 };
}

/** Canvas resolution. One texture for the whole board, not one per poster. */
const CANVAS_W = 2048;
const CANVAS_H = 1024;

const PAPER = "#e8dcc0";
const INK = "#2a2118";
const BRASS = "#d8b04a";

/** "tonight, 21:40" / "Thu 22:00" / "soon" — the wall speaks like a person. */
function when(iso: string | null): string {
  if (!iso) return "soon";
  const at = new Date(iso);
  const now = new Date();
  const time = at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (at.toDateString() === now.toDateString()) return `tonight, ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (at.toDateString() === tomorrow.toDateString()) return `tomorrow, ${time}`;
  return `${at.toLocaleDateString(undefined, { weekday: "short" })} ${time}`;
}

/** Break a line to fit, and say so with an ellipsis rather than overflowing. */
function fitted(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let cut = text;
  while (cut.length > 1 && ctx.measureText(`${cut}…`).width > maxWidth) {
    cut = cut.slice(0, -1);
  }
  return `${cut}…`;
}

export class PosterWallLayer {
  readonly group = new THREE.Group();
  private readonly canvas = document.createElement("canvas");
  private readonly texture: THREE.CanvasTexture;
  private readonly owned: Array<THREE.BufferGeometry | THREE.Material | THREE.Texture> = [];

  constructor(box: Aabb = POSTER_WALL, height = POSTER_WALL_HEIGHT) {
    this.canvas.width = CANVAS_W;
    this.canvas.height = CANVAS_H;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.owned.push(this.texture);

    const width = box.maxX - box.minX;
    const depth = box.maxZ - box.minZ;
    const cx = (box.minX + box.maxX) / 2;
    const cz = (box.minZ + box.maxZ) / 2;

    // The board: the same footprint movement collides with, made visible.
    // The Blender board faces +Z; the live paper faces the street to the
    // north. Fit the authored case to the existing collision volume so
    // the paper, point light and server-owned footprint stay aligned.
    const frame = instanceAuthoredAsset("frontier-notice-board", [
      { x: cx, y: 0, z: cz, width, height, depth, yaw: Math.PI },
    ]);
    this.group.add(frame);

    // The paper, a hair proud of the board's north face so it never z-fights.
    const paper = new THREE.Mesh(
      this.own(new THREE.PlaneGeometry(width - 0.22, height - 0.5)),
      this.own(
        new THREE.MeshStandardMaterial({
          map: this.texture,
          roughness: 0.95,
          emissive: 0xffffff,
          // Lit enough to read at night without looking like a screen.
          emissiveMap: this.texture,
          emissiveIntensity: 0.5,
        }),
      ),
    );
    paper.position.set(cx, height / 2 + 0.1, box.minZ - 0.02);
    paper.rotation.y = Math.PI;
    this.group.add(paper);

    // A lamp on the board so it reads from across the street.
    const lamp = new THREE.PointLight(0xffd9a0, 9, 12, 1.8);
    lamp.position.set(cx, height + 0.4, box.minZ - 0.8);
    this.group.add(lamp);

    this.paint([]);
  }

  private own<T extends THREE.BufferGeometry | THREE.Material | THREE.Texture>(thing: T): T {
    this.owned.push(thing);
    return thing;
  }

  /** Repaint the board. Cheap enough to call on every refresh of the bill. */
  setNights(nights: readonly NightBill[]): void {
    this.paint(nights.slice(0, SLOTS));
    this.texture.needsUpdate = true;
  }

  private paint(nights: readonly NightBill[]): void {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;

    ctx.fillStyle = "#1b1620";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    const headerH = 130;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = BRASS;
    ctx.font = "700 76px system-ui, sans-serif";
    ctx.letterSpacing = "18px";
    ctx.fillText("WHAT'S ON", CANVAS_W / 2, headerH / 2);
    ctx.letterSpacing = "0px";

    if (nights.length === 0) {
      ctx.fillStyle = "rgba(232,220,192,0.45)";
      ctx.font = "italic 46px system-ui, sans-serif";
      ctx.fillText("nothing billed — claim a room and book somebody", CANVAS_W / 2, CANVAS_H / 2);
      return;
    }

    const { columns, rows } = gridFor(nights.length);
    const cellW = CANVAS_W / columns;
    const cellH = (CANVAS_H - headerH) / rows;
    // One poster gets the middle of the board rather than the top left.
    const offsetX = columns === 1 ? CANVAS_W / 4 : 0;
    const cellWidth = columns === 1 ? CANVAS_W / 2 : cellW;
    nights.forEach((night, i) => {
      const col = i % columns;
      const row = Math.floor(i / columns);
      this.paintFlyer(
        ctx,
        night,
        offsetX + col * cellWidth,
        headerH + row * cellH,
        cellWidth,
        cellH,
      );
    });
  }

  /**
   * One flyer, in the palette its own materials produced — the same
   * arrangement ui/bench.ts draws in HTML, so the object on the wall and
   * the object in your Ledger are recognisably the same thing.
   */
  private paintFlyer(
    ctx: CanvasRenderingContext2D,
    night: NightBill,
    x: number,
    y: number,
    w: number,
    h: number,
  ): void {
    // Type scales with the cell so one big poster is genuinely legible
    // and eight small ones still fit their frames.
    const scale = Math.min(1.6, Math.max(0.72, h / 400));
    const pad = 22;
    const look = night.flyer?.look;
    const stock = look?.stock ?? PAPER;
    const ink = look?.ink ?? INK;
    const accent = look?.accent ?? BRASS;
    const inner = { x: x + pad, y: y + pad, w: w - pad * 2, h: h - pad * 2 };

    ctx.fillStyle = stock;
    ctx.fillRect(inner.x, inner.y, inner.w, inner.h);
    ctx.strokeStyle = accent;
    ctx.lineWidth = 4;
    ctx.strokeRect(inner.x, inner.y, inner.w, inner.h);

    const textPad = 26;
    const left = inner.x + textPad;
    const maxW = inner.w - textPad * 2;
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";

    // Rule, title, night, rule, footer — flyerCard's order.
    ctx.fillStyle = accent;
    ctx.fillRect(left, inner.y + 44 * scale, maxW, 5);

    ctx.fillStyle = ink;
    ctx.font = `750 ${Math.round(46 * scale)}px system-ui, sans-serif`;
    const title = night.flyer?.title || night.title || "an unnamed night";
    ctx.fillText(fitted(ctx, title.toUpperCase(), maxW), left, inner.y + 112 * scale);

    ctx.font = `${Math.round(30 * scale)}px system-ui, sans-serif`;
    ctx.globalAlpha = 0.85;
    ctx.fillText(fitted(ctx, night.artistName, maxW), left, inner.y + 158 * scale);
    ctx.fillText(fitted(ctx, when(night.scheduledFor), maxW), left, inner.y + 198 * scale);
    ctx.globalAlpha = 1;

    ctx.fillStyle = accent;
    ctx.fillRect(left, inner.y + 224 * scale, maxW, 5);

    ctx.fillStyle = ink;
    ctx.globalAlpha = 0.7;
    ctx.font = `${Math.round(24 * scale)}px system-ui, sans-serif`;
    // The line the whole system exists for: a maker's name, in the world.
    const foot = night.flyer
      ? `printed by ${night.flyer.makerName}`
      : `presented by ${night.hostName}`;
    ctx.fillText(fitted(ctx, foot, maxW), left, inner.y + 268 * scale);
    ctx.globalAlpha = 1;
  }

  dispose(): void {
    disposeAuthoredObject(this.group);
    for (const thing of this.owned) thing.dispose();
    this.owned.length = 0;
    this.group.clear();
  }
}
